import { useMemo } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ApplicationSelectModal } from '@/modals/ApplicationSelectModal';
import { useGetFundraisingApplicationList } from '@/query/queries/fundraising/useGetFundraisingApplicationList';
import { useGetMe } from '@/hooks/shared/useGetMe';
import { FR_APPLICATION_STATUSES, FR_STEPS } from '@/helpers/fundraising';
import { RoundButton } from '@/components/UI/RoundButton';
import { InfoMessage } from '@/components/InfoMessage';
import { useModal } from '@/providers/modal';
import { MODAL_IDS } from '@/kit/Modal/keys';
import IconEmpty from './icon_empty.svg';
import IconChevron from './chevron-right.svg';
import s from './index.module.scss';

export const ApplicationList = ({ setSelectedApplication }) => {
  const { data } = useGetFundraisingApplicationList();
  const { t } = useTranslation();
  const { openSingleModal } = useModal();
  const { isMainFounder  } = useGetMe();

  const onNewApplication = () => {
    openSingleModal(MODAL_IDS.application_select_modal);
  }

  const getApplicationStatusLabel = (status) => {
    switch (status) {
      case FR_APPLICATION_STATUSES.NEW:
        return t('fundraising:application_status.draft');
      case FR_APPLICATION_STATUSES.PRE_CHECKS:
      case FR_APPLICATION_STATUSES.IN_REVIEW:
      case FR_APPLICATION_STATUSES.RESUBMITTED:
        return t('fundraising:application_status.under_review');
      case FR_APPLICATION_STATUSES.CHANGES_REQUESTED:
        return t('fundraising:application_status.changes_requested');
      case FR_APPLICATION_STATUSES.APPROVED:
        return t('fundraising:application_status.approved');
      case FR_APPLICATION_STATUSES.REJECTED:
        return t('fundraising:application_status.rejected');
      default:
        return '';
    }
  };

  const groupedData = useMemo(
    () =>
      data?.reduce((acc, app, index) => {
        if (app.status === null) {
          return acc;
        }

        if (app.status === FR_APPLICATION_STATUSES.NEW) {
          acc.draft.push(app);
          return acc;
        }

        acc.active.push(app);

        if (app.status === FR_APPLICATION_STATUSES.APPROVED && index === 0) {
          // If the application is approved & and its upcoming show a message about fundraising goal
          app.step === FR_STEPS.awaiting_lender
            ? acc.messages.push({
                type: 'success',
                title: t('fundraising:app_messages.application_approved_sent_lender', {
                  round: app.fundraising_name,
                }),
                description: t('fundraising:app_messages.application_approved_sent_lender_desc'),
              })
            : acc.messages.push({
                type: 'success',
                title: t('fundraising:applications.fundraising_is_ready'),
                description: t('fundraising:applications.calculated_goal'),
              });
        }

        if (app.status === FR_APPLICATION_STATUSES.IN_REVIEW) {
          acc.messages.push({
            type: 'warning',
            title: t('fundraising:applications.application_under_review', {
              name: app.fundraising_name,
            }),
            description: t('fundraising:applications.reviewing_documents')
          });
        }

        return acc;
      }, { active: [], draft: [], messages: [] }), [data]
  );

  if (!groupedData) return null;

  const { active, draft, messages } = groupedData;

  const hasNewApplications = Boolean(data && active.length + draft.length < data.length);

  return (
    <div className={s.wrapper}>
      <div className="container-inner">
        <div className={s.header}>
          <div className={s.header_content}>
            <h1 className="f-hero-h2">{t('fundraising:applications.fundraising')}</h1>
            <p className="f-paragraph-regular">{t('fundraising:applications.view_and_manage')}</p>
          </div>
          {isMainFounder && (
            <RoundButton
              label={t('fundraising:applications.new_application')}
              onClick={onNewApplication}
              fillBackground
              disabled={!hasNewApplications}
              className={s.btn_new}
            />
          )}
        </div>
        {!!messages.length && (
          <div className={s.messages}>
            {messages.map((message, index) => (
              <InfoMessage
                key={index}
                type={message.type}
                title={message.title}
                description={message.description}
              />
            ))}
          </div>
        )}

        {(active.length || !draft.length) && (
          <div className={s.active}>
            <div className={classnames(s.title, 'f-article-accent')}>
              {t('fundraising:applications.active_applications')}
            </div>
            {active.length ? (
              <div className={s.list}>
                {active.map(app => (
                  <div
                    key={app.fundraising_id}
                    className={s.card}
                    onClick={() => setSelectedApplication(app)}
                  >
                    <div className="f-paragraph-accent" style={{ color: '#17232D' }}>
                      {t('fundraising:applications.round', { round: app.fundraising_name })}
                    </div>
                    <div className={s.controls}>
                      <div className={classnames(s.badge, app.status)}>
                        {getApplicationStatusLabel(app.status)}
                      </div>
                      <div className={s.chevron}>
                        <img src={IconChevron} alt="icon" width={20} height={20} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={s.active_empty}>
                <div className={s.active_empty_content}>
                  <div className={s.active_empty_image}>
                    <img src={IconEmpty} alt="icon" width={32} height={32} />
                  </div>
                  <div className={classnames(s.active_empty_title, 'f-article-accent')}>
                    {t('fundraising:applications.no_active_apps')}
                  </div>
                  <div className={classnames(s.active_empty_desc, 'f-small-regular')}>
                    {t('fundraising:applications.empty_desc')}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {!!draft.length && (
          <div className={s.draft}>
            <div className={classnames(s.title, 'f-article-accent')}>
              {t('fundraising:applications.draft')}
            </div>
            <div className={s.list}>
              {draft.map(app => (
                <div
                  key={app.fundraising_id}
                  className={s.card}
                  onClick={() => setSelectedApplication(app)}
                >
                  <div className="f-paragraph-accent" style={{ color: '#17232D' }}>
                    {t('fundraising:applications.round', { round: app.fundraising_name })}
                  </div>
                  <div className={s.controls}>
                    <div className={classnames(s.badge, app.status)}>
                      {getApplicationStatusLabel(app.status)}
                    </div>
                    <div className={s.chevron}>
                      <img src={IconChevron} alt="icon" width={20} height={20} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <ApplicationSelectModal onSuccess={app => setSelectedApplication(app)} />
    </div>
  );
};
